<template>
    <div v-if="storesPage || stores.length" class="stores">
        <page-cover
            v-if="storesPage.PageCover"
            :img="storesPage.PageCover.Image?.url ?? ''"
            :title="storesPage.PageCover.Title"
            :sub-title="storesPage.PageCover.Subtitle"
        />
        <div class="md:container mx-auto px-4 lg:px-12 py-8 md:py-12 lg:py-24">
            <div class="flex justify-between flex-wrap gap-8 mb-16">
                <form class="relative">
                    <select
                        v-model="selectedCategory"
                        class="dark category"
                        placeholder="Select..."
                    >
                        <option value="">Select a category</option>
                        <option
                            v-for="(option, index) in categories"
                            :key="index"
                            :value="option.Name"
                        >
                            {{ option.Name }}
                        </option>
                    </select>
                    <div
                        v-if="selectedCategory"
                        class="close"
                        @click="clearSelection"
                    >
                        <img
                            src="/images/icons/close.svg"
                            alt="close"
                            loading="lazy"
                        />
                    </div>
                    <div v-else class="dropdown">
                        <img
                            src="/images/icons/down-white.svg"
                            alt="down"
                            loading="lazy"
                        />
                    </div>
                </form>
                <form>
                    <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search a store"
                        class="search"
                    />
                </form>
            </div>
            <LetterFilter
                class="pb-4"
                @update-selection="handleUpdateSelection"
            />

            <div
                class="grid lg:grid-cols-4 md:grid-cols-3 grid-cols-2 gap-4 md:gap-6"
            >
                <storesPageCard
                    v-for="(storeCard, index) in filteredCards"
                    :key="index"
                    :title="storeCard.Name"
                    :logo-url="storeCard.LogoUrl?.url ?? ''"
                    :nav-to="`/stores/${storeCard.slug}`"
                    :show="storeCard.Active"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
const config = useRuntimeConfig();
useHead({
    title: 'Stores',
});
const { data: storesPage } = await useFetch(
    config.public.baseURl + '/stores-page'
);
const { data: stores } = await useFetch(
    config.public.baseURl + '/stores?_sort=Name',
    {
        transform: (stores) => {
            return stores.map((store) => ({
                Name: store.Name,
                LogoUrl: store.LogoUrl,
                slug: store.slug,
                Active: store.Active,
                categories: store.categories,
            }));
        },
        query: { Active: true },
    }
);
const { data: categories } = await useFetch(
    config.public.baseURl + '/categories'
);

const filters = ref([]);
const selectedCategory = ref('');

function filterByFirstLetter(item) {
    const filterLetter = item.Name.charAt(0).toUpperCase();
    return filters.value.includes(filterLetter);
}

const searchQuery = ref('');
const filteredCardsByLetter = computed(() => {
    if (stores.value && filters.value && filters.value.length > 0) {
        return stores.value.filter(filterByFirstLetter);
    } else if (stores.value) {
        return stores.value;
    } else {
        return [];
    }
});

const filteredCards = computed(() => {
    let result = filteredCardsByLetter.value;
    if (searchQuery.value.trim() !== '') {
        const searchRegex = new RegExp(searchQuery.value.trim(), 'i'); // Case-insensitive search
        result = result.filter((store) => searchRegex.test(store.Name));
    }
    if (selectedCategory.value !== '') {
        result = filterItemsByCategory(result, selectedCategory.value);
    }
    return result;
});

const handleUpdateSelection = (selections) => {
    filters.value = selections;
};
function filterItemsByCategory(items, selectedCategory) {
    const filteredItems = [];
    for (const item of items) {
        for (const category of item.categories) {
            if (category.Name === selectedCategory) {
                filteredItems.push(item);
                break;
            }
        }
    }
    return filteredItems;
}
function clearSelection() {
    selectedCategory.value = '';
}
</script>

<style lang="scss" scoped>
.stores {
    .close,
    .dropdown {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 12px;
        color: $white;
        width: 16px;
        height: 16px;
        cursor: pointer;
    }
    .category,
    .search {
        width: 280px;
    }
    .search {
        padding-left: 12px;
        background-image: url('/images/icons/search.svg');
        background-repeat: no-repeat !important;
        background-size: 18px !important;
        background-position: right 10px center !important;
    }
    @media (max-width: 767px),
        (min-device-width: 375px) and (max-device-width: 812px) and (-webkit-min-device-pixel-ratio: 3) {
        form {
            width: 100%;
            .category,
            .search {
                width: 100%;
                display: block;
            }
        }
    }
}
</style>
